// Google Custom Search Engine Service
export interface SearchResult {
  id: string
  title: string
  link: string
  snippet: string
  imageUrl?: string
  category?: string
  brand?: string
  price?: number
  displayLink: string
  formattedUrl: string
}

export interface SearchResponse {
  items: SearchResult[]
  searchInformation: {
    totalResults: string
    formattedTotalResults: string
    searchTime: number
  }
  queries: {
    request: Array<{
      searchTerms: string
      count: number
      startIndex: number
    }>
    nextPage?: Array<{
      searchTerms: string
      count: number
      startIndex: number
    }>
  }
}

class GoogleSearchService {
  private readonly API_KEY = 'AIzaSyBS1PiAfYj6rZjHtfeIJgJS9Tb261Sdtkw'
  private readonly SEARCH_ENGINE_ID = 'a3232b0041aa54c5f'
  private readonly BASE_URL = 'https://www.googleapis.com/customsearch/v1'
  
  private cache = new Map<string, { data: SearchResponse; timestamp: number }>()
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  async search(
    query: string, 
    options: {
      start?: number
      num?: number
      lr?: string
      hl?: string
      gl?: string
      safe?: string
    } = {}
  ): Promise<SearchResponse> {
    const cacheKey = `${query}-${options.start || 1}-${options.num || 10}`
    
    // Check cache first
    const cached = this.cache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('🔍 Using cached search results')
      return cached.data
    }

    try {
      const params = new URLSearchParams({
        key: this.API_KEY,
        cx: this.SEARCH_ENGINE_ID,
        q: query,
        start: (options.start || 1).toString(),
        num: Math.min(options.num || 10, 10).toString(), // Limit to 10 results
        lr: options.lr || 'lang_ar',
        hl: options.hl || 'ar',
        gl: options.gl || 'eg',
        safe: options.safe || 'off'
      })

      const url = `${this.BASE_URL}?${params.toString()}`
      console.log('🔍 Google CSE Request:', url)

      const response = await fetch(url)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Google CSE API Error:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
          url: url
        })
        
        // Handle specific error cases
        if (response.status === 400) {
          throw new Error(`Google CSE API error 400: Bad Request - ${errorText}`)
        } else if (response.status === 403) {
          throw new Error(`Google CSE API error 403: Forbidden - API Key may be invalid or quota exceeded`)
        } else if (response.status === 429) {
          throw new Error(`Google CSE API error 429: Too Many Requests - Quota exceeded`)
        } else {
          throw new Error(`Google CSE API error: ${response.status} ${response.statusText} - ${errorText}`)
        }
      }

      const data: any = await response.json()
      
      // توليد id فريد لكل نتيجة
      if (data.items) {
        data.items = data.items.map((item: any, index: number) => ({
          id: `google-${Date.now()}-${index}`,
          title: item.title || '',
          link: item.link || '',
          snippet: item.snippet || '',
          displayLink: item.displayLink || '',
          formattedUrl: item.formattedUrl || '',
          imageUrl: item.pagemap?.cse_image?.[0]?.src || 
                    item.pagemap?.cse_thumbnail?.[0]?.src || ''
        }))
      }
      
      // Cache the results
      this.cache.set(cacheKey, { data, timestamp: Date.now() })
      
      console.log('✅ Google CSE search completed:', {
        query,
        totalResults: data.searchInformation?.totalResults,
        itemsCount: data.items?.length || 0
      })

      return data as SearchResponse
    } catch (error) {
      console.error('❌ Google CSE search error:', error)
      throw error
    }
  }

  // Clear cache
  clearCache() {
    this.cache.clear()
    console.log('🗑️ Search cache cleared')
  }

  // Get cache size
  getCacheSize() {
    return this.cache.size
  }
}

export const googleSearchService = new GoogleSearchService()
